import React, { lazy, Suspense, useCallback, useMemo, useState, useEffect } from 'react';
import { Alert, Button, Card, DatePicker, Empty, Segmented, Select, Space, Spin, Tag, Tooltip } from 'antd';
import {
  ApiOutlined,
  ArrowRightOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  DollarOutlined,
  FileSyncOutlined,
  LinkOutlined,
  MoneyCollectOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  StopOutlined,
  TeamOutlined,
  TransactionOutlined,
  UserAddOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { getOverview } from '../../api/dashboard';
import { getTaobaoApiUsageDailyTrend, getTaobaoApiUsageOverview } from '../../api/taobaoIntegration';
import { useAuth } from '../../store/authContext';
import { ADMIN_ROLES } from '../../config/adminAccess';
import './index.css';
import { channelLabel } from '../../content/distributionChannels';

const DashboardChart = lazy(() => import('./DashboardChart'));

const platformOptions = [
  { value: 'TAOBAO', label: '淘宝' },
  { value: 'JD', label: '京东' },
];

const platformLabels = {
  TAOBAO: '淘宝',
  JD: '京东',
};

const dateRangePresetOptions = [
  { value: 'YEAR', label: '最近一年' },
  { value: 'QUARTER', label: '最近一个季度' },
  { value: 'MONTH', label: '最近一个月' },
  { value: 'WEEK', label: '最近一周' },
  { value: 'TODAY', label: '今天' },
  { value: 'CUSTOM', label: '自定义' },
];

const dateRangePresetLabels = Object.fromEntries(
  dateRangePresetOptions.map((option) => [option.value, option.label])
);

export const getPresetDateRange = (preset, referenceDate = dayjs()) => {
  const end = referenceDate.endOf('day');
  const startByPreset = {
    YEAR: end.subtract(1, 'year').add(1, 'day'),
    QUARTER: end.subtract(3, 'month').add(1, 'day'),
    MONTH: end.subtract(1, 'month').add(1, 'day'),
    WEEK: end.subtract(6, 'day'),
    TODAY: end,
  };
  const start = startByPreset[preset] || startByPreset.YEAR;
  return [start.startOf('day'), end];
};

const rebateStatusMeta = {
  PENDING: { label: '待确认', color: '#d97706', tone: 'amber' },
  CONFIRMED: { label: '已确认', color: '#2563eb', tone: 'blue' },
  SETTLED: { label: '已结算', color: '#059669', tone: 'green' },
  REFUNDED: { label: '已退款', color: '#dc2626', tone: 'red' },
};

const withdrawalStatusMeta = {
  PENDING: { label: '待审核', color: 'orange' },
  APPROVED: { label: '已批准', color: 'blue' },
  REJECTED: { label: '已拒绝', color: 'red' },
  COMPLETED: { label: '已完成', color: 'green' },
  FAILED: { label: '处理失败', color: 'volcano' },
};

const emptyOrderSummary = {
  orderCount: 0,
  orderAmount: 0,
  commissionAmount: 0,
  attributedOrderCount: 0,
  unattributedOrderCount: 0,
  waitingOrderCount: 0,
  settledOrderCount: 0,
  refundedOrderCount: 0,
};

const emptyDataQuality = {
  unattributedOrderCount: 0,
  missingCommissionOrderCount: 0,
  callbackCount: 0,
  failedCallbackCount: 0,
  reportImportCount: 0,
};

const formatCurrency = (value) => `¥${Number(value || 0).toLocaleString('zh-CN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatNumber = (value) => Number(value || 0).toLocaleString('zh-CN');

const normalizeUsageDays = (dailyTrend, from, to) => {
  const callsByDate = new Map((dailyTrend || []).map((item) => [
    dayjs(item.usage_date).format('YYYY-MM-DD'),
    {
      actualCalls: Number(item.actual_calls || 0),
      failures: Number(item.failures || 0),
      budgetBlocks: Number(item.budget_blocks || 0),
    },
  ]));
  const start = dayjs(from).startOf('day');
  const end = dayjs(to).startOf('day');
  const days = [];
  for (let cursor = start; cursor.valueOf() <= end.valueOf(); cursor = cursor.add(1, 'day')) {
    const date = cursor.format('YYYY-MM-DD');
    days.push({
      date,
      ...(callsByDate.get(date) || { actualCalls: 0, failures: 0, budgetBlocks: 0 }),
    });
  }
  return days;
};

export const buildDailyUsageTrend = (dailyTrend, from, to) => (
  normalizeUsageDays(dailyTrend, from, to).flatMap((item) => [
    { date: item.date, series: '实际请求', value: item.actualCalls },
    { date: item.date, series: '上游失败', value: item.failures },
    { date: item.date, series: '预算阻断', value: item.budgetBlocks },
  ])
);

export const buildCumulativeUsageTrend = (dailyTrend, from, to) => {
  let cumulativeCalls = 0;
  return normalizeUsageDays(dailyTrend, from, to).map((item) => {
    cumulativeCalls += item.actualCalls;
    return {
      date: item.date,
      series: '累计调用',
      cumulativeCalls,
    };
  });
};

const initialDateFilter = (searchParams) => {
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');
  if (dateFrom && dateTo) {
    const dateRange = [dayjs(dateFrom), dayjs(dateTo)];
    if (dateRange.every((date) => date.isValid())) {
      const preset = dateRangePresetOptions
        .filter((option) => option.value !== 'CUSTOM')
        .find((option) => getPresetDateRange(option.value).every(
          (date, index) => date.isSame(dateRange[index], 'day')
        ))?.value;
      return { dateRange, datePreset: preset || 'CUSTOM' };
    }
  }
  return { dateRange: getPresetDateRange('YEAR'), datePreset: 'YEAR' };
};

const MetricCard = ({ icon, title, value, detail, tone, onClick, ariaLabel }) => {
  const content = (
    <>
      <span className={`dashboard-metric__icon dashboard-metric__icon--${tone}`}>{icon}</span>
      <span className="dashboard-metric__content">
        <span className="dashboard-metric__title">{title}</span>
        <strong className="dashboard-metric__value">{value}</strong>
        <span className="dashboard-metric__detail">{detail}</span>
      </span>
      {onClick ? <ArrowRightOutlined className="dashboard-metric__arrow" /> : null}
    </>
  );

  return onClick ? (
    <button type="button" className="dashboard-metric dashboard-metric--clickable" onClick={onClick} aria-label={ariaLabel}>
      {content}
    </button>
  ) : (
    <div className="dashboard-metric">{content}</div>
  );
};

const SectionHeader = ({ title, description, action }) => (
  <div className="dashboard-section__header">
    <div>
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
    </div>
    {action}
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({
    ...initialDateFilter(searchParams),
    platform: searchParams.get('platform') || '',
  }));
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [taobaoUsage, setTaobaoUsage] = useState(null);
  const [taobaoNinetyDayUsage, setTaobaoNinetyDayUsage] = useState(null);
  const [taobaoUsageLoading, setTaobaoUsageLoading] = useState(false);
  const [taobaoUsageError, setTaobaoUsageError] = useState('');
  const [taobaoTrendError, setTaobaoTrendError] = useState('');
  const canViewTaobaoUsage = admin?.role === ADMIN_ROLES.SUPER_ADMIN;

  const requestParams = useMemo(() => ({
    dateFrom: appliedFilters.dateRange?.[0]?.format('YYYY-MM-DD'),
    dateTo: appliedFilters.dateRange?.[1]?.format('YYYY-MM-DD'),
    platform: appliedFilters.platform || undefined,
  }), [appliedFilters]);
  const taobaoUsageRange = useMemo(() => ({
    from: dayjs().subtract(89, 'day').format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
  }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getOverview(requestParams));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [requestParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchTaobaoUsage = useCallback(async () => {
    if (!canViewTaobaoUsage) return;
    setTaobaoUsageLoading(true);
    setTaobaoUsageError('');
    setTaobaoTrendError('');
    try {
      const [periodResult, trendResult] = await Promise.allSettled([
        getTaobaoApiUsageOverview(taobaoUsageRange),
        getTaobaoApiUsageDailyTrend(taobaoUsageRange),
      ]);
      if (periodResult.status === 'fulfilled') {
        setTaobaoUsage(periodResult.value);
      } else {
        setTaobaoUsageError(periodResult.reason?.message || '淘宝调用统计加载失败');
      }
      if (trendResult.status === 'fulfilled') {
        setTaobaoNinetyDayUsage(trendResult.value);
      } else {
        setTaobaoTrendError(trendResult.reason?.message || '近3个月趋势加载失败');
      }
    } catch (error) {
      setTaobaoUsageError(error?.message || '淘宝调用统计加载失败');
    } finally {
      setTaobaoUsageLoading(false);
    }
  }, [canViewTaobaoUsage, taobaoUsageRange]);

  useEffect(() => {
    fetchTaobaoUsage();
  }, [fetchTaobaoUsage]);

  const applyFilters = () => {
    const nextFilters = { ...filters };
    setAppliedFilters(nextFilters);
    const params = {};
    if (nextFilters.dateRange?.[0] && nextFilters.dateRange?.[1]) {
      params.dateFrom = nextFilters.dateRange[0].format('YYYY-MM-DD');
      params.dateTo = nextFilters.dateRange[1].format('YYYY-MM-DD');
    }
    if (nextFilters.platform) {
      params.platform = nextFilters.platform;
    }
    setSearchParams(params);
  };

  const changeDatePreset = (datePreset) => {
    setFilters((previous) => ({
      ...previous,
      datePreset,
      dateRange: datePreset === 'CUSTOM'
        ? previous.dateRange
        : getPresetDateRange(datePreset),
    }));
  };

  const buildDetailUrl = (pathname, detailParams = {}) => {
    const params = new URLSearchParams();
    Object.entries(detailParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    const query = params.toString();
    return query ? `${pathname}?${query}` : pathname;
  };

  const openRebates = (status) => navigate(buildDetailUrl('/admin/rebates', {
    status,
    orderTimeFrom: requestParams.dateFrom,
    orderTimeTo: requestParams.dateTo,
    platform: requestParams.platform,
  }));

  const openOrders = (extra = {}) => navigate(buildDetailUrl('/admin/order-tracking', {
    tab: 'orders',
    orderTimeFrom: requestParams.dateFrom,
    orderTimeTo: requestParams.dateTo,
    platform: requestParams.platform,
    ...extra,
  }));

  const orderSummary = data?.orderSummary || emptyOrderSummary;
  const rebateSummary = data?.rebateSummary || { recordCount: 0, actualRebateAmount: 0, statuses: [] };
  const withdrawalSummary = data?.withdrawalSummary || { recordCount: 0, amount: 0, statuses: [] };
  const quality = data?.dataQualitySummary || emptyDataQuality;
  const platformSummaries = data?.platformSummaries || [];
  const channelSummaries = data?.channelSummaries || [];
  const periodLabel = requestParams.dateFrom && requestParams.dateTo
    ? `${dateRangePresetLabels[appliedFilters.datePreset] || '自定义'} · ${requestParams.dateFrom} 至 ${requestParams.dateTo}`
    : '全部时间';

  const rebateChartData = (rebateSummary.statuses || []).map((item) => ({
    ...item,
    statusLabel: rebateStatusMeta[item.status]?.label || item.status,
  }));
  const platformChartData = platformSummaries.map((item) => ({
    ...item,
    platformLabel: platformLabels[item.platform] || item.platform || '未知平台',
  }));

  const rebatePieConfig = {
    data: rebateChartData,
    angleField: 'recordCount',
    colorField: 'statusLabel',
    innerRadius: 0.64,
    height: 248,
    legend: false,
    label: false,
    color: rebateChartData.map((item) => rebateStatusMeta[item.status]?.color || '#64748b'),
    tooltip: { title: 'statusLabel' },
  };
  const platformColumnConfig = {
    data: platformChartData,
    xField: 'platformLabel',
    yField: 'orderCount',
    height: 248,
    color: '#2563eb',
    axis: { y: { title: false }, x: { title: false } },
    style: { radiusTopLeft: 4, radiusTopRight: 4, maxWidth: 52 },
    tooltip: { title: 'platformLabel' },
  };
  const channelChartData = channelSummaries.map((item) => ({
    ...item,
    channelLabel: channelLabel(item.channel),
  }));
  const channelColumnConfig = {
    data: channelChartData,
    xField: 'channelLabel',
    yField: 'userCount',
    height: 248,
    color: '#16a34a',
    axis: { y: { title: false }, x: { title: false } },
    style: { radiusTopLeft: 4, radiusTopRight: 4, maxWidth: 52 },
    tooltip: { title: 'channelLabel' },
  };
  const dailyUsageTrend = buildDailyUsageTrend(
    taobaoUsage?.dailyTrend,
    taobaoUsageRange.from,
    taobaoUsageRange.to
  );
  const cumulativeUsageTrend = buildCumulativeUsageTrend(
    taobaoNinetyDayUsage,
    taobaoUsageRange.from,
    taobaoUsageRange.to
  );
  const dailyUsageLineConfig = {
    data: dailyUsageTrend,
    xField: 'date',
    yField: 'value',
    colorField: 'series',
    height: 240,
    smooth: true,
    color: ['#1677ff', '#ef4444', '#f59e0b'],
    axis: {
      x: { title: false, tickCount: 6, labelFormatter: (value) => dayjs(value).format('MM-DD') },
      y: { title: false, labelFormatter: formatNumber },
    },
    legend: { color: { position: 'top' } },
    tooltip: { title: (datum) => datum.date },
    style: { lineWidth: 2 },
  };
  const cumulativeUsageLineConfig = {
    data: cumulativeUsageTrend,
    xField: 'date',
    yField: 'cumulativeCalls',
    height: 240,
    smooth: true,
    color: '#14b8a6',
    axis: {
      x: { title: false, tickCount: 6, labelFormatter: (value) => dayjs(value).format('MM-DD') },
      y: { title: false, labelFormatter: formatNumber },
    },
    tooltip: { title: (datum) => datum.date },
    style: { lineWidth: 3 },
  };
  const ninetyDayTotal = cumulativeUsageTrend[cumulativeUsageTrend.length - 1]?.cumulativeCalls || 0;

  return (
    <div className="dashboard-page">
      <header className="dashboard-page__header">
        <div>
          <h2>经营总览</h2>
          <p>用户、订单、返利与提现数据集中查看</p>
        </div>
        <Tag color="blue">统计周期：{periodLabel}</Tag>
      </header>

      <section className="dashboard-filter" aria-label="仪表盘筛选">
        <div className="dashboard-filter__title">
          <AuditOutlined />
          <span>统计条件</span>
        </div>
        <Space wrap size={12}>
          <Segmented
            aria-label="快捷统计周期"
            value={filters.datePreset}
            options={dateRangePresetOptions}
            onChange={changeDatePreset}
          />
          {filters.datePreset === 'CUSTOM' ? (
            <DatePicker.RangePicker
              aria-label="自定义统计日期"
              value={filters.dateRange}
              format="YYYY-MM-DD"
              allowClear={false}
              onChange={(dateRange) => setFilters((previous) => ({
                ...previous,
                dateRange: dateRange || previous.dateRange,
              }))}
            />
          ) : null}
          <Select
            aria-label="统计平台"
            placeholder="全部平台"
            allowClear
            value={filters.platform || undefined}
            options={platformOptions}
            onChange={(platform) => setFilters((previous) => ({ ...previous, platform: platform || '' }))}
            style={{ width: 136 }}
          />
          <Button type="primary" icon={<ReloadOutlined />} onClick={applyFilters}>刷新数据</Button>
        </Space>
      </section>

      <Spin spinning={loading}>
        <section className="dashboard-metric-grid" aria-label="核心经营指标">
          <MetricCard
            icon={<UserOutlined />}
            title="总用户"
            value={formatNumber(data?.totalUsers)}
            detail={`周期新增 ${formatNumber(data?.periodNewUsers)}`}
            tone="blue"
            onClick={() => navigate('/admin/users')}
            ariaLabel="查看用户明细"
          />
          <MetricCard
            icon={<UserAddOutlined />}
            title="周期新增用户"
            value={formatNumber(data?.periodNewUsers)}
            detail={`今日新增 ${formatNumber(data?.todayNewUsers)}`}
            tone="cyan"
          />
          <MetricCard
            icon={<CrownOutlined />}
            title="有效付费会员"
            value={formatNumber(data?.totalMembers)}
            detail={`周期收入 ${formatCurrency(data?.periodMembershipRevenue)}`}
            tone="amber"
            onClick={() => navigate('/admin/memberships')}
            ariaLabel="查看会员明细"
          />
          <MetricCard
            icon={<ShoppingCartOutlined />}
            title="订单数"
            value={formatNumber(orderSummary.orderCount)}
            detail={`成交额 ${formatCurrency(orderSummary.orderAmount)}`}
            tone="indigo"
            onClick={() => openOrders()}
            ariaLabel="查看订单明细"
          />
          <MetricCard
            icon={<DollarOutlined />}
            title="平台佣金"
            value={formatCurrency(orderSummary.commissionAmount)}
            detail={`已归因 ${formatNumber(orderSummary.attributedOrderCount)} 单`}
            tone="green"
          />
          <MetricCard
            icon={<MoneyCollectOutlined />}
            title="筛选返利"
            value={formatCurrency(rebateSummary.actualRebateAmount)}
            detail={`${formatNumber(rebateSummary.recordCount)} 条 · 累计结算 ${formatCurrency(data?.totalRebate)}`}
            tone="purple"
            onClick={() => openRebates()}
            ariaLabel="查看返利明细"
          />
          <MetricCard
            icon={<TransactionOutlined />}
            title="待处理提现"
            value={formatCurrency(data?.pendingWithdrawal)}
            detail={`${formatNumber(data?.pendingWithdrawalCount)} 笔待审核`}
            tone="red"
            onClick={() => navigate(buildDetailUrl('/admin/withdrawals', {
              status: 'PENDING',
              dateFrom: requestParams.dateFrom,
              dateTo: requestParams.dateTo,
            }))}
            ariaLabel="查看待处理提现"
          />
          <MetricCard
            icon={<FileSyncOutlined />}
            title="周期会员收入"
            value={formatCurrency(data?.periodMembershipRevenue)}
            detail={`累计收入 ${formatCurrency(data?.totalMembershipRevenue)}`}
            tone="slate"
          />
        </section>

        {canViewTaobaoUsage ? (
          <Card className="dashboard-panel dashboard-taobao-panel">
            <SectionHeader
              title="淘宝 API 调用"
              description={`独立统计最近90天（${taobaoUsageRange.from} 至 ${taobaoUsageRange.to}）；仅计算实际请求淘宝的调用`}
              action={(
                <Button type="link" onClick={() => navigate(buildDetailUrl('/admin/taobao-api-usage', taobaoUsageRange))}>
                  查看调用明细 <ArrowRightOutlined />
                </Button>
              )}
            />
            {taobaoUsageError ? (
              <Alert showIcon type="warning" message={taobaoUsageError} />
            ) : (
              <Spin spinning={taobaoUsageLoading}>
                <div className="dashboard-taobao-summary" aria-label="淘宝调用核心指标">
                  <div><ApiOutlined /><span>实际请求</span><strong>{formatNumber(taobaoUsage?.summary?.actual_calls)}</strong></div>
                  <div><CheckCircleOutlined /><span>成功请求</span><strong>{formatNumber(taobaoUsage?.summary?.successes)}</strong></div>
                  <div><WarningOutlined /><span>上游失败</span><strong className="value-red">{formatNumber(taobaoUsage?.summary?.failures)}</strong></div>
                  <div><StopOutlined /><span>预算阻断</span><strong className="value-amber">{formatNumber(taobaoUsage?.summary?.budget_blocks)}</strong></div>
                  <div><TeamOutlined /><span>涉及用户</span><strong>{formatNumber(taobaoUsage?.summary?.unique_users)}</strong></div>
                </div>
                <div className="dashboard-taobao-charts">
                  <section className="dashboard-taobao-chart" aria-label="每日调用趋势">
                    <div className="dashboard-taobao-chart__header">
                      <div>
                        <h4>每日调用趋势</h4>
                        <span>最近90天的请求、失败与预算阻断</span>
                      </div>
                    </div>
                    <Suspense fallback={<div className="dashboard-chart__loading">图表加载中</div>}>
                      <DashboardChart type="line" config={dailyUsageLineConfig} />
                    </Suspense>
                  </section>
                  <section className="dashboard-taobao-chart" aria-label="近90天累计调用趋势">
                    <div className="dashboard-taobao-chart__header">
                      <div>
                        <h4>近90天累计调用趋势</h4>
                        <span>{taobaoUsageRange.from} 至 {taobaoUsageRange.to}</span>
                      </div>
                      <strong>{formatNumber(ninetyDayTotal)}</strong>
                    </div>
                    {taobaoTrendError ? (
                      <Alert showIcon type="warning" message={taobaoTrendError} />
                    ) : (
                      <Suspense fallback={<div className="dashboard-chart__loading">图表加载中</div>}>
                        <DashboardChart type="line" config={cumulativeUsageLineConfig} />
                      </Suspense>
                    )}
                  </section>
                </div>
                <div className="dashboard-taobao-methods">
                  <div>
                    <h4>请求最多的方法</h4>
                    <div className="dashboard-taobao-list dashboard-taobao-list--methods">
                      <div className="dashboard-taobao-list__head"><span>TOP 方法</span><span>请求</span><span>失败</span></div>
                      {(taobaoUsage?.methodBreakdown || []).slice(0, 5).map((item) => (
                        <button
                          type="button"
                          key={item.dimension}
                          onClick={() => navigate(buildDetailUrl('/admin/taobao-api-usage', {
                            ...taobaoUsageRange,
                            method: item.dimension,
                          }))}
                        >
                          <span>{item.dimension}</span>
                          <strong>{formatNumber(item.call_count)}</strong>
                          <span>{formatNumber(item.failures)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Spin>
            )}
          </Card>
        ) : null}

        <div className="dashboard-two-column">
          <Card className="dashboard-panel">
            <SectionHeader title="订单与归因" description="订单进度与用户关系覆盖情况" />
            <div className="dashboard-stat-list">
              <button type="button" onClick={() => openOrders()}>
                <span>全部订单</span><strong>{formatNumber(orderSummary.orderCount)}</strong>
              </button>
              <button type="button" onClick={() => openOrders({ attributionSource: 'ATTRIBUTED' })}>
                <span>已归因</span><strong className="value-green">{formatNumber(orderSummary.attributedOrderCount)}</strong>
              </button>
              <button type="button" onClick={() => openOrders({ attributionSource: 'UNATTRIBUTED' })}>
                <span>未归因</span><strong className="value-red">{formatNumber(orderSummary.unattributedOrderCount)}</strong>
              </button>
              <button type="button" onClick={() => openOrders({ orderStatus: 'WAITING_CONFIRMATION' })}>
                <span>待确认</span><strong className="value-amber">{formatNumber(orderSummary.waitingOrderCount)}</strong>
              </button>
              <button type="button" onClick={() => openOrders({ sourceSettled: '1' })}>
                <span>渠道已结算</span><strong>{formatNumber(orderSummary.settledOrderCount)}</strong>
              </button>
              <button type="button" onClick={() => openOrders({ orderStatus: 'REFUNDED' })}>
                <span>已退款</span><strong>{formatNumber(orderSummary.refundedOrderCount)}</strong>
              </button>
            </div>
          </Card>

          <Card className="dashboard-panel">
            <SectionHeader title="数据质量" description="优先处理会影响归因和返利的数据" />
            <div className="dashboard-quality-grid">
              <button type="button" onClick={() => openOrders({ attributionSource: 'UNATTRIBUTED' })}>
                <WarningOutlined />
                <span>未归因订单</span>
                <strong>{formatNumber(quality.unattributedOrderCount)}</strong>
              </button>
              <button type="button" onClick={() => openOrders({ commissionState: 'MISSING' })}>
                <DollarOutlined />
                <span>佣金缺失</span>
                <strong>{formatNumber(quality.missingCommissionOrderCount)}</strong>
              </button>
              <button type="button" onClick={() => navigate(buildDetailUrl('/admin/order-tracking', {
                tab: 'logs',
                logPlatform: requestParams.platform,
                processingResult: 'FAILED',
                logTimeFrom: requestParams.dateFrom,
                logTimeTo: requestParams.dateTo,
              }))}>
                <LinkOutlined />
                <span>失败回调</span>
                <strong>{formatNumber(quality.failedCallbackCount)}</strong>
              </button>
              <div>
                <CheckCircleOutlined />
                <span>回调 / 导入批次</span>
                <strong>{formatNumber(quality.callbackCount)} / {formatNumber(quality.reportImportCount)}</strong>
              </div>
            </div>
          </Card>
        </div>

        <div className="dashboard-two-column dashboard-two-column--analytics">
          <Card className="dashboard-panel dashboard-panel--analytics">
            <SectionHeader
              title="返利状态"
              description={`${formatNumber(rebateSummary.recordCount)} 条记录 · ${formatCurrency(rebateSummary.actualRebateAmount)}`}
              action={<Button type="link" onClick={() => openRebates()}>查看明细 <ArrowRightOutlined /></Button>}
            />
            <div className="dashboard-chart-layout">
              <div className="dashboard-chart">
                {rebateChartData.some((item) => Number(item.recordCount) > 0)
                  ? (
                    <Suspense fallback={<div className="dashboard-chart__loading">图表加载中</div>}>
                      <DashboardChart type="pie" config={rebatePieConfig} />
                    </Suspense>
                  )
                  : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前条件暂无返利" />}
              </div>
              <div className="dashboard-status-list">
                {rebateChartData.map((item) => (
                  <button
                    type="button"
                    key={item.status}
                    aria-label={`查看${item.statusLabel}返利明细`}
                    onClick={() => openRebates(item.status)}
                  >
                    <i style={{ background: rebateStatusMeta[item.status]?.color }} />
                    <span>{item.statusLabel}</span>
                    <strong>{formatNumber(item.recordCount)}</strong>
                    <small>{formatCurrency(item.amount)}</small>
                    <ArrowRightOutlined />
                  </button>
                ))}
              </div>
            </div>
          </Card>

          <Card className="dashboard-panel dashboard-panel--analytics">
            <SectionHeader
              title="提现状态"
              description={`${formatNumber(withdrawalSummary.recordCount)} 笔申请 · ${formatCurrency(withdrawalSummary.amount)}`}
              action={<Button type="link" onClick={() => navigate(buildDetailUrl('/admin/withdrawals', {
                status: 'ALL',
                dateFrom: requestParams.dateFrom,
                dateTo: requestParams.dateTo,
              }))}>查看明细 <ArrowRightOutlined /></Button>}
            />
            <div className="dashboard-withdrawal-list">
              {(withdrawalSummary.statuses || []).map((item) => {
                const meta = withdrawalStatusMeta[item.status] || { label: item.status, color: 'default' };
                return (
                  <button type="button" key={item.status} onClick={() => navigate(buildDetailUrl('/admin/withdrawals', {
                    status: item.status,
                    dateFrom: requestParams.dateFrom,
                    dateTo: requestParams.dateTo,
                  }))}>
                    <Tag color={meta.color}>{meta.label}</Tag>
                    <strong>{formatNumber(item.recordCount)} 笔</strong>
                    <span>{formatCurrency(item.amount)}</span>
                    <ArrowRightOutlined />
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        <Card className="dashboard-panel dashboard-platform-panel">
          <SectionHeader
            title="注册渠道"
            description="当前统计周期内各分发渠道的新用户来源"
            action={<Tag>{formatNumber(data?.periodNewUsers)} 位新用户</Tag>}
          />
          {channelChartData.length ? (
            <div className="dashboard-platform-layout">
              <div className="dashboard-platform-chart">
                <Suspense fallback={<div className="dashboard-chart__loading">图表加载中</div>}>
                  <DashboardChart type="column" config={channelColumnConfig} />
                </Suspense>
              </div>
              <div className="dashboard-platform-table">
                <div className="dashboard-platform-table__head"><span>渠道</span><span>注册人数</span><span>占比</span><span /><span /></div>
                {channelChartData.map((item) => (
                  <button type="button" key={item.channel} onClick={() => navigate(`/admin/users?registrationChannel=${item.channel}`)}>
                    <strong>{item.channelLabel}</strong>
                    <span>{formatNumber(item.userCount)}</span>
                    <span>{Number(item.percentage || 0).toFixed(2)}%</span>
                    <span /><ArrowRightOutlined />
                  </button>
                ))}
              </div>
            </div>
          ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前周期暂无新用户" />}
        </Card>

        <Card className="dashboard-panel dashboard-platform-panel">
          <SectionHeader
            title="平台表现"
            description="对比各平台订单规模、佣金与归因覆盖"
            action={<Tag>{platformSummaries.length} 个平台</Tag>}
          />
          {platformChartData.length ? (
            <div className="dashboard-platform-layout">
              <div className="dashboard-platform-chart">
                <Suspense fallback={<div className="dashboard-chart__loading">图表加载中</div>}>
                  <DashboardChart type="column" config={platformColumnConfig} />
                </Suspense>
              </div>
              <div className="dashboard-platform-table">
                <div className="dashboard-platform-table__head">
                  <span>平台</span><span>订单</span><span>成交额</span><span>佣金</span><span>归因率</span>
                </div>
                {platformChartData.map((item) => {
                  const attributionRate = item.orderCount
                    ? Math.round((Number(item.attributedOrderCount || 0) / Number(item.orderCount)) * 100)
                    : 0;
                  return (
                    <button type="button" key={item.platform || 'UNKNOWN'} onClick={() => openOrders({ platform: item.platform })}>
                      <strong>{item.platformLabel}</strong>
                      <span>{formatNumber(item.orderCount)}</span>
                      <span>{formatCurrency(item.orderAmount)}</span>
                      <span>{formatCurrency(item.commissionAmount)}</span>
                      <Tooltip title={`${formatNumber(item.attributedOrderCount)} 单已归因`}>
                        <span className="dashboard-rate">{attributionRate}%</span>
                      </Tooltip>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前条件暂无平台订单" />
          )}
        </Card>
      </Spin>
    </div>
  );
};

export default Dashboard;
